import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, Card, CardContent, Typography, TextField, Button, CircularProgress, 
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScaleIcon from '@mui/icons-material/Scale';
import DiamondIcon from '@mui/icons-material/Diamond';
import dayjs from 'dayjs';
import { 
  getDashboardData, 
  setDashboardFilters
} from '../redux/actions/dashboard/dashboardAction';
import { fetchDepartments } from '../api/department/departmentApi';

const DashboardContent = () => {
  const dispatch = useDispatch();
  const { 
    loading, 
    departmentWiseProduction,
    departmentWisePending,
    batchSummary,
    filters
  } = useSelector((state) => state.dashboard);
  
  const [departments, setDepartments] = useState([]);

  // Build params based on current filters - always fetch all data for dashboard
  const buildParams = useCallback((dateFrom, dateTo) => {
    const today = dayjs().format('YYYY-MM-DD');
    const isToday = dateFrom === today && dateTo === today;
    
    const params = {
      limit: 0 // Fetch all data
    };
    
    if (isToday) {
      params.today_only = true;
    } else {
      params.date_from = dateFrom;
      params.date_to = dateTo;
      params.today_only = false;
    }
    
    return params;
  }, []);

  // Fetch departments on mount
  useEffect(() => {
    fetchDepartments()
      .then(response => {
        setDepartments(response.data || []);
      })
      .catch(error => {
        console.error('Error fetching departments:', error);
      });
  }, []);

  // Fetch dashboard data only on component mount with default filters
  useEffect(() => {
    const params = buildParams(filters.dateFrom, filters.dateTo);
    dispatch(getDashboardData(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Only run on mount

  // Handle date change
  const handleDateChange = useCallback((field, value) => {
    dispatch(setDashboardFilters({ [field]: value }));
  }, [dispatch]);

  // Handle apply button click
  const handleApply = useCallback(() => {
    const params = buildParams(filters.dateFrom, filters.dateTo);
    dispatch(getDashboardData(params));
  }, [dispatch, filters.dateFrom, filters.dateTo, buildParams]);

  // Handle reset button click
  const handleReset = useCallback(() => {
    const today = dayjs().format('YYYY-MM-DD');
    dispatch(setDashboardFilters({ dateFrom: today, dateTo: today }));
    const params = buildParams(today, today);
    dispatch(getDashboardData(params));
  }, [dispatch, buildParams]);

  // Calculate department-wise production statistics
  // Logic: 
  // - Pending: Batches currently at this department (is_latest_movement = 1 and to_department_id = this dept)
  // - Completed: Batches that have moved FROM this department TO A DIFFERENT department
  //   (Only count movements between different departments, not within the same department)
  const departmentStats = useMemo(() => {
    if (!departmentWiseProduction || departmentWiseProduction.length === 0) {
      return {};
    }

    // Track batches by department to avoid double counting
    const batchTracker = new Map(); // key: imported_inventory_id, value: { currentDept, completedFromDepts: Set, weight, stones }

    // First pass: Process all movements to track department relationships
    departmentWiseProduction.forEach(item => {
      const batchId = item.imported_inventory_id;
      const toDept = item.department_name || 'Unknown';
      const fromDept = item.from_department_name || 'Unknown';
      const isLatest = item.is_latest_movement === 1 || item.is_latest_movement === '1';

      if (!batchTracker.has(batchId)) {
        batchTracker.set(batchId, {
          currentDept: null,
          completedFromDepts: new Set(), // Only departments this batch has moved FROM to a DIFFERENT department
          weight: parseFloat(item.bip_net_weight) || parseFloat(item.net_weight) || 0,
          stones: parseFloat(item.stone_qty) || parseFloat(item.issue_tot_qty) || 0
        });
      }

      const batch = batchTracker.get(batchId);
      
      // Track current department (latest movement)
      if (isLatest && toDept !== 'Unknown') {
        batch.currentDept = toDept;
      }

      // Track departments this batch has moved FROM to a DIFFERENT department
      // Only count as "completed" if the batch moved from one department to a different one
      if (fromDept !== 'Unknown' && toDept !== 'Unknown' && fromDept !== toDept) {
        batch.completedFromDepts.add(fromDept);
      }
    });

    // Second pass: Calculate stats for each department
    const stats = {};

    batchTracker.forEach((batch, batchId) => {
      // Count as pending for current department
      if (batch.currentDept) {
        if (!stats[batch.currentDept]) {
          stats[batch.currentDept] = {
            batches: { total: 0, pending: 0, completed: 0 },
            weight: { total: 0, pending: 0, completed: 0 },
            stones: { total: 0, pending: 0, completed: 0 }
          };
        }
        stats[batch.currentDept].batches.pending++;
        stats[batch.currentDept].weight.pending += batch.weight;
        stats[batch.currentDept].stones.pending += batch.stones;
      }

      // Count as completed for departments this batch has moved FROM to a DIFFERENT department
      batch.completedFromDepts.forEach(fromDept => {
        if (!stats[fromDept]) {
          stats[fromDept] = {
            batches: { total: 0, pending: 0, completed: 0 },
            weight: { total: 0, pending: 0, completed: 0 },
            stones: { total: 0, pending: 0, completed: 0 }
          };
        }
        stats[fromDept].batches.completed++;
        stats[fromDept].weight.completed += batch.weight;
        stats[fromDept].stones.completed += batch.stones;
      });
    });

    // Add unscanned batches to OPD's pending count
    // Unscanned batches are in departmentWisePending data with status = 'Unscanned'
    if (departmentWisePending && departmentWisePending.length > 0) {
      const unscannedBatches = departmentWisePending.filter(item => {
        const status = (item.status || '').trim();
        return status === 'Unscanned';
      });

      if (unscannedBatches.length > 0) {
        // Initialize OPD if it doesn't exist
        if (!stats['OPD']) {
          stats['OPD'] = {
            batches: { total: 0, pending: 0, completed: 0 },
            weight: { total: 0, pending: 0, completed: 0 },
            stones: { total: 0, pending: 0, completed: 0 }
          };
        }

        // Add unscanned batches to OPD's pending
        unscannedBatches.forEach(item => {
          stats['OPD'].batches.pending++;
          stats['OPD'].weight.pending += parseFloat(item.net_weight) || 0;
          stats['OPD'].stones.pending += parseFloat(item.issue_tot_qty) || parseFloat(item.stone_qty) || 0;
        });
      }
    }

    // Calculate totals for each department
    Object.keys(stats).forEach(deptName => {
      const dept = stats[deptName];
      dept.batches.total = dept.batches.pending + dept.batches.completed;
      dept.weight.total = dept.weight.pending + dept.weight.completed;
      dept.stones.total = dept.stones.pending + dept.stones.completed;
    });

    return stats;
  }, [departmentWiseProduction, departmentWisePending]);

  // Sort departments by process_order_no (ascending order)
  const sortedDepartmentNames = useMemo(() => {
    // Create a map from department data if available, otherwise use fetched departments
    const deptMap = new Map();
    
    // First, try to get process_order_no from production data
    if (departmentWiseProduction && departmentWiseProduction.length > 0) {
      departmentWiseProduction.forEach(item => {
        const deptName = item.department_name;
        if (deptName && item.department_process_order !== undefined) {
          deptMap.set(deptName, item.department_process_order);
        }
      });
    }
    
    // Fallback to fetched departments list
    departments.forEach(d => {
      if (!deptMap.has(d.name)) {
        deptMap.set(d.name, d.process_order_no || 999);
      }
    });
    
    return Object.keys(departmentStats).sort((a, b) => {
      const orderA = deptMap.get(a) || 999;
      const orderB = deptMap.get(b) || 999;
      return orderA - orderB;
    });
  }, [departmentStats, departments, departmentWiseProduction]);

  // Calculate overall summary
  // Logic:
  // - Total: Confirmed total batches from batchSummary (imported_inventory where is_confirm=1)
  // - Completed: Fetched from batchSummary (imported_inventory table where batch_no_status=2)
  // - Pending: Total confirmed batches - Completed batches
  const overallSummary = useMemo(() => {
    const summary = {
      batches: { total: 0, pending: 0, completed: 0 },
      weight: { total: 0, pending: 0, completed: 0 },
      stones: { total: 0, pending: 0, completed: 0 }
    };

    // Use batchSummary for all calculations (confirmed batches)
    if (batchSummary) {
      // Total: Confirmed total batches from batchSummary
      summary.batches.total = parseInt(batchSummary.total_batches) || 0;
      summary.weight.total = parseFloat(batchSummary.total_weight) || 0;
      summary.stones.total = parseFloat(batchSummary.total_stone_qty) || 0;

      // Completed: From batchSummary (imported_inventory table where batch_no_status=2)
      summary.batches.completed = parseInt(batchSummary.completed_batches) || 0;
      summary.weight.completed = parseFloat(batchSummary.completed_weight) || 0;
      summary.stones.completed = parseFloat(batchSummary.completed_stone_qty) || 0;

      // Pending = Total confirmed batches - Completed batches
      summary.batches.pending = summary.batches.total - summary.batches.completed;
      summary.weight.pending = summary.weight.total - summary.weight.completed;
      summary.stones.pending = summary.stones.total - summary.stones.completed;
    }

    return summary;
  }, [batchSummary]);

  // Helper function to format numbers
  const formatNumber = (value, decimals = 0) => {
    const num = parseFloat(value);
    if (isNaN(num)) return (0).toFixed(decimals);
    return num.toFixed(decimals);
  };

  // Get department color based on index
  const getDepartmentColor = (index) => {
    const colors = [
      { primary: '#1976d2', secondary: '#42a5f5' },
      { primary: '#388e3c', secondary: '#66bb6a' },
      { primary: '#f57c00', secondary: '#ffb74d' },
      { primary: '#7b1fa2', secondary: '#ba68c8' },
      { primary: '#d32f2f', secondary: '#ef5350' },
      { primary: '#0288d1', secondary: '#4fc3f7' },
      { primary: '#5d4037', secondary: '#8d6e63' },
      { primary: '#c2185b', secondary: '#e91e63' }
    ];
    return colors[index % colors.length];
  };

  // Render department card with better UI
  const renderDepartmentCard = (deptName, stats, index) => {
    const colors = getDepartmentColor(index);
    const completionRate = stats.batches.total > 0 
      ? (stats.batches.completed / stats.batches.total) * 100 
      : 0;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={deptName}>
        <Card 
          sx={{ 
            height: '100%',
            boxShadow: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: 'white',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Department Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {deptName}
              </Typography>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <InventoryIcon sx={{ fontSize: 24 }} />
              </Box>
            </Box>

            <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />

            {/* Batches Section */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 'medium' }}>
                  Batches
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {stats.batches.total}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Pending
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5, color: '#ffeb3b' }}>
                    {stats.batches.pending}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Completed
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5, color: '#c8e6c9' }}>
                    {stats.batches.completed}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />

            {/* Weight Section */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScaleIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 'medium' }}>
                  Weight (g)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem' }}>
                    {formatNumber(stats.weight.total, 3)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Pending
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem', color: '#ffeb3b' }}>
                    {formatNumber(stats.weight.pending, 3)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Completed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem', color: '#c8e6c9' }}>
                    {formatNumber(stats.weight.completed, 3)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />

            {/* Stones Section */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DiamondIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 'medium' }}>
                  Stones
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem' }}>
                    {formatNumber(stats.stones.total, 0)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Pending
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem', color: '#ffeb3b' }}>
                    {formatNumber(stats.stones.pending, 0)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                    Completed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1rem', color: '#c8e6c9' }}>
                    {formatNumber(stats.stones.completed, 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.3)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'medium' }}>
                  Completion Rate
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
                  {formatNumber(completionRate, 1)}%
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  width: '100%', 
                  height: 8, 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  borderRadius: 4,
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    width: `${completionRate}%`,
                    height: '100%',
                    backgroundColor: '#c8e6c9',
                    transition: 'width 0.3s ease',
                    borderRadius: 4
                  }} 
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
          Department-wise Production Dashboard
        </Typography>
      </Box>
      
      {/* Date Filter Bar */}
      <Card sx={{ mb: 3, p: 2, boxShadow: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content', fontWeight: 'medium' }}>
            Filter by Date:
          </Typography>
          <TextField
            label="From Date"
            type="date"
            value={filters.dateFrom}
            onChange={e => handleDateChange('dateFrom', e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={filters.dateTo}
            onChange={e => handleDateChange('dateTo', e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={handleApply}
            sx={{ minWidth: 120 }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={{ minWidth: 120 }}
          >
            Reset
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic' }}>
            {filters.dateFrom === filters.dateTo && filters.dateFrom === dayjs().format('YYYY-MM-DD') 
              ? 'Showing today\'s transaction data' 
              : `Showing data from ${dayjs(filters.dateFrom).format('MMM DD, YYYY')} to ${dayjs(filters.dateTo).format('MMM DD, YYYY')}`}
          </Typography>
        </Box>
      </Card>

      {/* Department Cards */}
      {sortedDepartmentNames.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {sortedDepartmentNames.map((deptName, index) => 
            renderDepartmentCard(deptName, departmentStats[deptName], index)
          )}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No department data found for the selected date range
          </Typography>
        </Box>
      )}

      {/* Overall Summary Card */}
      <Card sx={{ boxShadow: 4, borderRadius: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: 'white' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Overall Summary
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(255, 255, 255, 0.2)', color: 'white' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InventoryIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                      Batches
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {overallSummary.batches.total}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#ffeb3b' }}>
                    {overallSummary.batches.pending}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#c8e6c9' }}>
                    {overallSummary.batches.completed}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScaleIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                      Weight (g)
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatNumber(overallSummary.weight.total, 3)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#ffeb3b' }}>
                    {formatNumber(overallSummary.weight.pending, 3)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#c8e6c9' }}>
                    {formatNumber(overallSummary.weight.completed, 3)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DiamondIcon sx={{ fontSize: 18, mr: 1, opacity: 0.9 }} />
                      Stones
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatNumber(overallSummary.stones.total, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#ffeb3b' }}>
                    {formatNumber(overallSummary.stones.pending, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#c8e6c9' }}>
                    {formatNumber(overallSummary.stones.completed, 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardContent;
